import { useContext, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format, differenceInDays, parseISO, Locale } from 'date-fns';
import { es, enUS, ca, pt, de, fr } from 'date-fns/locale';
import {
    Button,
    Card,
    CardContent,
    IconButton,
    Chip,
    Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
    PowerSettingsNew,
    CheckCircle,
    Warning,
    CalendarToday,
    CreditCard,
    EventRepeat,
    Receipt,
} from '@mui/icons-material';
import { getPath } from '../../Pages';
import { AppContext } from '../../../sporttia/AppContext';
import { SttTable } from '../../../sporttia/all';
import { useCrud, useLoadingBar } from '../../../lib/hooks';
import Contact from '../../public/Contact';
import { formatPriceByLocale } from '../../../lib/utils';

type SportCenter = {
    id: number;
    name: string;
    customer: {
        id: number;
    };
    city?: {
        country?: {
            currency?: string;
            currencySymbol?: string;
        };
    };
};

type SubscriptionType = {
    id: number | null;
    status: string;
    dateini: string;
    dateend: string;
    plan: string;
    fee: number;
    feeInterval: string;
};

type Licence = {
    id: number;
    ini: string;
    end: string;
    paymentForm?: string;
    price: number;
    trash: boolean;
    inExpirationPeriod: boolean;
};

type LicencesResponse = {
    rows: Licence[];
    count: number;
};

// Locale mapping for date-fns
const localeMap: Record<string, Locale> = {
    es: es,
    en: enUS,
    ca: ca,
    pt: pt,
    de: de,
    fr: fr,
    mx: es,
};

export default function Subscription(): React.ReactElement {
    const navigate = useNavigate();
    const cxt = useContext(AppContext)!;
    const { t, i18n } = useTranslation();

    const [, , , Get, Post] = useCrud(undefined, false);

    const [sportCenter, setSportCenter] = useState<SportCenter>();
    const [currentSubscription, setCurrentSubscription] =
        useState<SubscriptionType>({
            id: null,
            status: '',
            dateini: '',
            dateend: '',
            plan: '',
            fee: 0,
            feeInterval: '',
        });
    const [licences, setLicences] = useState<LicencesResponse>();
    const [ready, setReady] = useState<boolean>(false);
    const currentDate = new Date();

    const [_showLoading, hideLoading, loadingBar] = useLoadingBar(true);

    // Get locale for date formatting
    const dateLocale = useMemo(
        () => localeMap[i18n.language] || es,
        [i18n.language],
    );

    // Format date - hides year if current year
    const formatDate = (date: string): string => {
        const d = parseISO(date);
        const isCurrentYear = d.getFullYear() === new Date().getFullYear();
        return format(d, isCurrentYear ? 'd MMM' : 'd MMM yyyy', {
            locale: dateLocale,
        });
    };

    // Use useMemo for derived value instead of useEffect + setState
    const subIsValid = useMemo(() => {
        if (!licences?.rows) {
            return false;
        }
        return licences.rows.reduce(
            (result, item) =>
                (!item.trash && !item.inExpirationPeriod) || result,
            false,
        );
    }, [licences]);

    useEffect(() => {
        cxt?.setHeader({
            title: t('subscription.title'),
        });
    }, []);

    // Extract primitive value for dependency
    const scId = cxt?.sc?.id;
    useEffect(() => {
        if (scId && !isNaN(scId)) {
            loadSportCenterData();
        }
    }, [scId]);

    // Extract primitive value for dependency
    const sportCenterId = sportCenter?.customer?.id;
    useEffect(() => {
        if (sportCenterId) {
            loadSubscriptionData();
        }
    }, [sportCenterId]);

    // Extract primitive value for dependency
    const subscriptionId = currentSubscription.id;
    useEffect(() => {
        if (subscriptionId) {
            loadLicenses();
        }
    }, [subscriptionId]);

    useEffect(() => {
        if (ready) {
            hideLoading();
        }
    }, [ready]);

    const loadSportCenterData = (): void => {
        Get(`/scs/${cxt?.sc?.id}`).then((response: any) => {
            setSportCenter(response.sc);
        });
    };

    const loadSubscriptionData = (): void => {
        Get(`/customers/${sportCenter!.customer.id}/subscriptions`, {
            page: 1,
            rows: 100,
        }).then((response: any) => {
            if (response.count > 0) {
                setCurrentSubscription(getCurrentSubscription(response.rows));
            } else {
                setReady(true);
            }
        });
    };

    function loadLicenses(): void {
        // Fetch all licenses for local pagination
        Get(`/subscriptions/${currentSubscription.id}/licences`, {
            page: 1,
            rows: 1000,
        })
            .then((response: any) => {
                // Sort by period (end date) descending
                const sortedRows = [...response.rows].sort(
                    (a: Licence, b: Licence) =>
                        parseISO(b.end).getTime() - parseISO(a.end).getTime(),
                );
                setLicences({
                    ...response,
                    rows: sortedRows,
                });
            })
            .finally(() => {
                setReady(true);
            });
    }

    function getCurrentSubscription(
        subscriptions: SubscriptionType[],
    ): SubscriptionType {
        return (
            subscriptions.find(
                (sub) =>
                    currentDate >= new Date(sub.dateini) &&
                    currentDate <= new Date(sub.dateend),
            ) || {
                id: null,
                status: '',
                dateini: '',
                dateend: '',
                plan: '',
                fee: 0,
                feeInterval: '',
            }
        );
    }

    function getLatestLicence(licencesData: LicencesResponse): Licence | null {
        if (!licencesData.rows || licencesData.rows.length === 0) {
            return null;
        }
        return (
            [...licencesData.rows].sort(
                (a: Licence, b: Licence) =>
                    parseISO(b.end).getTime() - parseISO(a.end).getTime(),
            )[0] ?? null
        );
    }

    function logout(): void {
        Post('/logout', {}, true).then(() => {
            cxt?.setLoggedUser(false);
            cxt?.setHeader(null);
        });
    }

    // Translate fee interval from date format (e.g., "0000-01-00 00:00:00" = 1 month)
    const translateFeeInterval = (interval: string | undefined): string => {
        if (!interval) return t('intervals.monthly');

        // Parse date format: YYYY-MM-DD HH:MM:SS
        const match = interval.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (match && match[1] && match[2] && match[3]) {
            const years = parseInt(match[1], 10);
            const months = parseInt(match[2], 10);
            const days = parseInt(match[3], 10);

            if (years === 1 && months === 0 && days === 0) {
                return t('intervals.yearly');
            }
            if (years === 0 && months === 6 && days === 0) {
                return t('intervals.biannual');
            }
            if (years === 0 && months === 3 && days === 0) {
                return t('intervals.quarterly');
            }
            if (years === 0 && months === 1 && days === 0) {
                return t('intervals.monthly');
            }
            if (years === 0 && months === 0 && days === 7) {
                return t('intervals.weekly');
            }
            if (years === 0 && months === 0 && days === 1) {
                return t('intervals.daily');
            }
        }

        // Fallback: try as text key
        const intervalKey = interval.toLowerCase();
        const translationKey = `intervals.${intervalKey}`;
        const translated = t(translationKey);
        if (translated !== translationKey) {
            return translated;
        }

        return t('intervals.monthly');
    };

    const latest = licences && getLatestLicence(licences);
    const daysRemaining = currentSubscription.dateend
        ? differenceInDays(parseISO(currentSubscription.dateend), new Date())
        : 0;

    if (!ready) {
        return <Box>{loadingBar}</Box>;
    }

    if (!currentSubscription.id) {
        return (
            <>
                <Card className="text-center p-8">
                    <Warning color="warning" className="text-[64px] mb-4" />
                    <Typography variant="h5" gutterBottom>
                        {t('subscription.title')}
                    </Typography>
                    <Typography variant="body1" color="textSecondary" paragraph>
                        {t('subscription.noSubscription')}
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => navigate(getPath('help'))}
                    >
                        {t('common.help')}
                    </Button>
                    <IconButton className="ml-4" onClick={logout}>
                        <PowerSettingsNew color="error" />
                    </IconButton>
                </Card>
                <Box className="mt-8">
                    <Contact />
                </Box>
            </>
        );
    }

    return (
        <>
            <Box>{loadingBar}</Box>

            {/* Status Banner */}
            <Card className="mb-6 overflow-hidden">
                <Box
                    className={`p-6 text-white ${
                        subIsValid
                            ? 'bg-gradient-to-br from-green-600 to-green-500'
                            : 'bg-gradient-to-br from-red-600 to-red-500'
                    }`}
                >
                    <Box className="flex items-center justify-between">
                        <Box className="flex items-center">
                            {subIsValid ? (
                                <CheckCircle className="mr-3 text-[40px]" />
                            ) : (
                                <Warning className="mr-3 text-[40px]" />
                            )}
                            <Box>
                                <Typography
                                    variant="h5"
                                    className="font-semibold"
                                >
                                    {t('subscription.title')}
                                </Typography>
                                <Chip
                                    label={t(
                                        subIsValid
                                            ? 'subscription.active'
                                            : 'subscription.inactive',
                                    )}
                                    size="small"
                                    className="mt-2 bg-white/20 text-white font-medium"
                                />
                            </Box>
                        </Box>
                    </Box>

                    {subIsValid && (
                        <Box className="mt-4 flex items-center bg-white/10 rounded-lg p-3">
                            <CalendarToday className="mr-3 text-[24px]" />
                            <Box>
                                <Typography
                                    variant="body2"
                                    className="opacity-80"
                                >
                                    {t('subscription.daysToExpire')}
                                </Typography>
                                <Typography variant="h6" className="font-bold">
                                    {daysRemaining} {t('generic.days')}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    className="opacity-70"
                                >
                                    {format(
                                        parseISO(currentSubscription.dateend),
                                        'd MMMM yyyy',
                                        { locale: dateLocale },
                                    )}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    {!subIsValid && latest && (
                        <Box className="mt-4 bg-white/10 rounded-lg p-3">
                            <Typography variant="body2" className="opacity-80">
                                {t('subscription.expiredAt')}
                            </Typography>
                            <Typography variant="h6" className="font-bold">
                                {format(parseISO(latest.end), 'dd/MM/yyyy')}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Card>

            {/* Plan Details Card */}
            <Card className="mb-6">
                <CardContent className="p-6">
                    <Typography
                        variant="h6"
                        color="primary"
                        className="font-semibold mb-4"
                    >
                        {t('subscription.planDetails')}
                    </Typography>

                    <Grid container spacing={3}>
                        {/* Plan */}
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Box className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                <Box className="p-2 bg-primary-50 rounded-lg">
                                    <Receipt color="primary" />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {t('subscription.plan')}
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        className="font-semibold"
                                    >
                                        {t(
                                            `plans.${currentSubscription.plan}`,
                                        ) || currentSubscription.plan}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Fee */}
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Box className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                <Box className="p-2 bg-green-50 rounded-lg">
                                    <CreditCard color="success" />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {t('subscription.fee')}
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        className="font-semibold"
                                    >
                                        {formatPriceByLocale(
                                            currentSubscription.fee,
                                            cxt?.sc?.city?.country?.currency,
                                        )}{' '}
                                        /{' '}
                                        {translateFeeInterval(
                                            currentSubscription.feeInterval,
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>

                        {/* Period */}
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <Box className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                                <Box className="p-2 bg-blue-50 rounded-lg">
                                    <EventRepeat color="info" />
                                </Box>
                                <Box>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        {t('subscription.period')}
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        className="font-semibold"
                                    >
                                        {formatDate(
                                            currentSubscription.dateini,
                                        )}{' '}
                                        -{' '}
                                        {formatDate(
                                            currentSubscription.dateend,
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>

                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={2}
                        className="mt-6"
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            fullWidth
                            onClick={() =>
                                navigate(getPath('subscriptionPlans'))
                            }
                        >
                            {t('subscription.changePlan')}
                        </Button>
                    </Stack>
                </CardContent>
            </Card>

            {/* Licenses Table */}
            <Card>
                <CardContent className="p-6">
                    <Typography
                        variant="h6"
                        color="primary"
                        className="font-semibold mb-4"
                    >
                        {t('subscription.licenceHistory')}
                    </Typography>
                    <SttTable
                        data={licences}
                        loading={false}
                        autoload={false}
                        localPagination
                        perPage={20}
                        columns={[
                            {
                                title: t('subscription.period'),
                                align: 'left',
                                value: (row: Licence) => {
                                    const isActive =
                                        currentDate >= new Date(row.ini) &&
                                        currentDate <= new Date(row.end);
                                    return (
                                        <Box className="flex items-center gap-2">
                                            <Typography variant="body2">
                                                {formatDate(row.ini)} -{' '}
                                                {formatDate(row.end)}
                                            </Typography>
                                            {isActive && (
                                                <Chip
                                                    label={t(
                                                        'subscription.active',
                                                    )}
                                                    size="small"
                                                    color="success"
                                                    variant="outlined"
                                                />
                                            )}
                                        </Box>
                                    );
                                },
                            },
                            {
                                title: t('subscription.plan'),
                                align: 'center',
                                value: () =>
                                    t(`plans.${currentSubscription.plan}`) ||
                                    currentSubscription.plan,
                            },
                            {
                                title: t('subscription.status'),
                                align: 'center',
                                value: (row: Licence) =>
                                    row.paymentForm ? (
                                        <Chip
                                            label={t('subscription.paid')}
                                            size="small"
                                            color="success"
                                            variant="filled"
                                        />
                                    ) : (
                                        <Chip
                                            label={t('subscription.pending')}
                                            size="small"
                                            color="warning"
                                            variant="filled"
                                        />
                                    ),
                            },
                            {
                                title: t('subscription.price'),
                                field: 'price',
                                align: 'right',
                                value: (row: Licence) =>
                                    formatPriceByLocale(
                                        row.price,
                                        cxt?.sc?.city?.country?.currency,
                                    ),
                            },
                        ]}
                    />
                </CardContent>
            </Card>

            {/* Cancel Subscription Button */}
            <Box className="mt-6 text-center">
                <Button
                    variant="text"
                    color="error"
                    onClick={() => {
                        if (subIsValid) {
                            navigate(getPath('help'));
                        } else {
                            window.open(
                                'https://www.sporttia.com/es/contactar/',
                                '_blank',
                            );
                        }
                    }}
                >
                    {t(
                        subIsValid
                            ? 'subscription.cancel'
                            : 'subscription.renewLicence',
                    )}
                </Button>
            </Box>
        </>
    );
}
